/**
 * SuggestTopicWidget — reusable inline widget for the top of any topic
 * list page. Lets any visitor type an entity name and either:
 *   - jump to an existing article (with autocomplete suggestions), OR
 *   - kick off AI generation for a brand-new topic.
 *
 * Wires to the Global Knowledge Graph (`/api/kg/find-or-create`).
 *
 * Props:
 * - `entityType` — one of person | place | organization | event | disease
 * - `label`      — visible heading ("Suggest a topic", "Add a place", …)
 * - `placeholder`— input placeholder
 * - `hint`       — optional disambiguation hint appended to the AI prompt
 * - `linkPrefix` — URL prefix for jumping to entities (defaults from entityType)
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, Loader2, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";

const DEFAULT_PREFIX = {
  person: "/person", place: "/place", organization: "/organization",
  event: "/event", disease: "/disease",
};

export default function SuggestTopicWidget({
  entityType,
  label = "Suggest a topic",
  placeholder = "Type any topic…",
  hint,
  linkPrefix,
}) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [busy, setBusy] = useState(false);
  const prefix = linkPrefix || DEFAULT_PREFIX[entityType];

  // Debounced suggestion fetch
  useEffect(() => {
    if (!q.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await http.get("/kg/suggest", { params: { q, entity_type: entityType } });
        setSuggestions(r.data?.items || []);
      } catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, entityType]);

  const generate = async () => {
    const name = q.trim();
    if (!name) return;
    setBusy(true);
    try {
      const r = await http.post("/kg/find-or-create", { name, entity_type: entityType, hint });
      const e = r.data?.entity;
      if (e?.slug) {
        nav(`${prefix}/${e.slug}`);
      }
    } catch {
      /* toast handled via axios interceptor */
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="wofy-card !p-4 mb-8 border-2"
      style={{ borderColor: "var(--wofy-primary)", background: "rgba(178,93,56,0.04)" }}
      data-testid={`suggest-${entityType}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }}/>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs opacity-70 hidden md:inline">— we'll write the article with AI on your behalf</div>
      </div>
      <div className="relative">
        <div className="flex items-center gap-2 wofy-card !p-2">
          <Search className="w-4 h-4 opacity-60 ml-1"/>
          <input
            value={q}
            onChange={e => { setQ(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 200)}
            onKeyDown={e => { if (e.key === "Enter") generate(); }}
            placeholder={placeholder}
            className="flex-1 bg-transparent outline-none text-sm"
            data-testid="suggest-input"
          />
          <button
            onClick={generate}
            disabled={!q.trim() || busy}
            className="wofy-btn-primary text-sm inline-flex items-center gap-1.5"
            data-testid="suggest-generate"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
            {busy ? "Preparing…" : "Generate"}
          </button>
        </div>

        {showSug && suggestions.length > 0 && (
          <ul
            className="absolute left-0 right-0 mt-1 wofy-card !p-1 z-30 shadow-lg max-h-64 overflow-auto"
            data-testid="suggest-dropdown"
          >
            {suggestions.map(s => (
              <li key={s.slug}>
                <button
                  onMouseDown={(e) => { e.preventDefault(); nav(`${prefix}/${s.slug}`); setShowSug(false); }}
                  className="w-full text-left px-3 py-2 rounded hover:bg-black/5 text-sm inline-flex items-center gap-2 justify-between"
                >
                  <span>
                    {s.name}
                    {s.aliases?.length ? <span className="ml-2 text-xs opacity-60">also: {s.aliases.slice(0,2).join(" · ")}</span> : null}
                  </span>
                  <ArrowRight className="w-3 h-3 opacity-60"/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
